import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const RANGE_OPTIONS = [
  { value: '6m', label: 'Last 6 months' },
  { value: 'fy', label: 'This financial year' },
  { value: 'all', label: 'All time' },
];

function rupees(paise) {
  return `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const SPLIT_META = {
  pending_payment: { label: 'Approved, payment on the way', color: 'var(--blue)' },
  open:            { label: 'Waiting for your invoice',      color: 'var(--amber)' },
  disputed:        { label: 'Question raised, needs your reply', color: 'var(--violet)' },
  confirmed:       { label: 'Submitted, admin reviewing',     color: 'var(--green)' },
  failed:          { label: 'Payment failed, retry needed',   color: 'var(--red)' },
};

// Activity dot colour + icon per event, matching the status colours used on
// the Clients and Billing pages so each kind of update is easy to tell apart.
const ICON = {
  check: <path d="M20 6L9 17l-5-5" />,
  cross: <path d="M18 6L6 18M6 6l12 12" />,
  userMinus: <><circle cx="9" cy="8" r="3.5" /><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1M16 11h6" /></>,
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
  rupee: <path d="M6 4h12M6 9h12M14 20L7 13h3a4 4 0 0 0 0-9" />,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></>,
};
const ACTIVITY_META = {
  'client:active':        { bg: 'rgba(22,199,154,.12)', fg: '#0D8E6E', icon: 'check' },
  'client:declined':      { bg: 'rgba(219,39,119,.1)',  fg: '#BE185D', icon: 'cross' },
  'client:removed':       { bg: 'rgba(147,51,234,.1)',  fg: '#7E22CE', icon: 'userMinus' },
  'client:revoked':       { bg: 'rgba(224,72,77,.1)',   fg: '#C43D42', icon: 'cross' },
  'message:message':      { bg: 'rgba(124,92,255,.11)', fg: '#5B3FD1', icon: 'chat' },
  'cycle:pending_payment':{ bg: 'rgba(47,107,255,.1)',  fg: '#2456D6', icon: 'check' },
  'cycle:paid':           { bg: 'rgba(22,199,154,.12)', fg: '#0D8E6E', icon: 'rupee' },
  'cycle:failed':         { bg: 'rgba(224,72,77,.1)',   fg: '#C43D42', icon: 'alert' },
};
const ACTIVITY_DEFAULT = { bg: 'rgba(139,149,166,.14)', fg: '#5A667A', icon: 'clock' };

export default function PartnerHome() {
  const api = useApi();
  const { partner, counts } = useOutletContext();
  const [overview, setOverview] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('6m');

  useEffect(() => {
    let alive = true;
    api.partnerOverview(range)
      .then((ov) => alive && setOverview(ov))
      .catch((err) => alive && setError(err.response?.data?.error || err.message));
    return () => { alive = false; };
  }, [api, range]);

  useEffect(() => {
    let alive = true;
    api.partnerListTiers()
      .then((t) => alive && setTiers(t.items || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  if (error) return <div className="admin-banner error" style={{ margin: '20px 36px' }}>{error}</div>;
  if (!partner || !overview) return <p className="muted" style={{ margin: '20px 36px' }}>Loading…</p>;

  const totalCommission = overview.monthlyTrend.reduce((s, m) => s + m.commissionPaise, 0);
  const totalEntries = overview.monthlyTrend.reduce((s, m) => s + m.entryCount, 0);
  const bestMonth = overview.monthlyTrend.reduce((a, b) => (b.commissionPaise > a.commissionPaise ? b : a), overview.monthlyTrend[0]);
  const pendingPayment = overview.statusSplit.find((s) => s.status === 'pending_payment')?.amountPaise || 0;

  const maxVal = Math.max(1, ...overview.monthlyTrend.map((m) => m.commissionPaise));
  const n = Math.max(1, overview.monthlyTrend.length);
  const barW = Math.max(14, Math.min(52, Math.floor((640 - (n + 1) * 8) / n)));
  const gap = (640 - n * barW) / (n + 1);
  const bars = overview.monthlyTrend.map((m, i) => {
    const x = gap + i * (barW + gap);
    const h = Math.round((m.commissionPaise / maxVal) * 120);
    return { ...m, x, y: 150 - h, h, isLast: i === n - 1 };
  });
  const maxBar = bars.reduce((a, b) => (b.commissionPaise > a.commissionPaise ? b : a), bars[0]);

  const maxTierPct = tiers.length ? Math.max(...tiers.map((t) => Number(t.commission_percentage))) : 30;
  const tierPct = Number(partner.tier_commission_percentage || 0);
  const gaugeFraction = maxTierPct > 0 ? Math.min(1, tierPct / maxTierPct) : 0;
  const gaugeOffset = Math.round(251 * (1 - gaugeFraction));

  const currentTierIdx = tiers.findIndex((t) => t.id === partner.tier_id);
  const nextTier = currentTierIdx >= 0 ? tiers[currentTierIdx + 1] : null;
  const activeClients = counts?.active || 0;

  const splitTotal = overview.statusSplit.reduce((s, x) => s + x.amountPaise, 0) || 1;

  return (
    <>
      <header className="bar">
        <div>
          <h1>Overview</h1>
          <p>Your earnings, clients and payout status at a glance.</p>
        </div>
        <div className="bar-side">
          <SearchableSelect
            value={range}
            onChange={setRange}
            options={RANGE_OPTIONS}
            showAll={false}
            btnClass="sel"
            wrapStyle={{ width: 190 }}
            matchTriggerWidth
          />
        </div>
      </header>

      <section className="view">
        <div className="hero">
          <div className="panel earn">
            <div className="earn-head">
              <div>
                <div className="earn-label">Commission earned, {(RANGE_OPTIONS.find((o) => o.value === range) || {}).label.toLowerCase()}</div>
                <div className="earn-fig num">
                  {rupees(totalCommission)}
                  {bestMonth && bestMonth.commissionPaise > 0 && (
                    <span className="delta">+{rupees(bestMonth.commissionPaise)} best month</span>
                  )}
                </div>
                <div className="earn-sub">
                  Across {totalEntries} client payment{totalEntries === 1 ? '' : 's'}
                  {pendingPayment > 0 && ` · ${rupees(pendingPayment)} approved and awaiting transfer`}
                </div>
              </div>
            </div>
            {totalCommission > 0 ? (
              <>
                <div className="chart-wrap">
                  <svg viewBox="0 0 640 170" preserveAspectRatio="none" role="img" aria-label="Monthly commission chart">
                    <defs>
                      <linearGradient id="barg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2F6BFF" stopOpacity=".85"/><stop offset="1" stopColor="#16C79A" stopOpacity=".55"/></linearGradient>
                      <linearGradient id="barnow" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E29A0B"/><stop offset="1" stopColor="#E29A0B" stopOpacity=".55"/></linearGradient>
                    </defs>
                    <line x1="0" y1="150" x2="640" y2="150" stroke="#E6EAF1" strokeWidth="1"/>
                    <line x1="0" y1="100" x2="640" y2="100" stroke="#F0F3F7" strokeWidth="1"/>
                    <line x1="0" y1="50" x2="640" y2="50" stroke="#F0F3F7" strokeWidth="1"/>
                    {bars.map((b) => (
                      <rect key={b.month} x={b.x} y={b.y} width={barW} height={Math.max(b.h, 2)} rx="5" fill={b.isLast ? 'url(#barnow)' : 'url(#barg)'} />
                    ))}
                    {maxBar && maxBar.commissionPaise > 0 && (
                      <text x={maxBar.x + barW / 2} y={Math.max(18, maxBar.y - 8)} textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontSize="13" fontWeight="600" fill="#16202F">
                        {rupees(maxBar.commissionPaise)}
                      </text>
                    )}
                  </svg>
                </div>
                <div className="mlabels">
                  {bars.map((b) => {
                    const [, mo] = b.month.split('-');
                    return <span key={b.month} className={b.isLast ? 'now' : ''}>{MONTH_SHORT[Number(mo) - 1]}</span>;
                  })}
                </div>
              </>
            ) : (
              <div className="chart-empty">
                {range === 'all'
                  ? "No commission earned yet — it'll show up here once a client makes a payment."
                  : `No commission earned in ${(RANGE_OPTIONS.find((o) => o.value === range) || {}).label.toLowerCase()}.`}
              </div>
            )}
          </div>

          <div className="panel tier-card">
            <div className="tier-top">
              <span className="chip-tier"><i></i>{partner.tier_name ? `${partner.tier_name} tier` : 'No tier assigned'}</span>
              {partner.tier_expires_at && (
                <span className="muted" style={{ fontSize: 12.5, marginLeft: 'auto' }}>
                  Valid to {new Date(partner.tier_expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
            <div className="gauge">
              <svg viewBox="0 0 200 118">
                <defs><linearGradient id="gg" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#2F6BFF"/><stop offset="1" stopColor="#16C79A"/></linearGradient></defs>
                <path d="M20 106 A 80 80 0 0 1 180 106" fill="none" stroke="#EDF0F5" strokeWidth="16" strokeLinecap="round"/>
                <path d="M20 106 A 80 80 0 0 1 180 106" fill="none" stroke="url(#gg)" strokeWidth="16" strokeLinecap="round" strokeDasharray="251" strokeDashoffset={gaugeOffset}/>
              </svg>
              <div className="gauge-mid"><b className="num">{tierPct}%</b><small>commission rate</small></div>
            </div>
            <div className="tier-foot">
              <div>Active clients<b>{activeClients}</b></div>
              <div>Next tier<b>{nextTier ? `${nextTier.name} · ${nextTier.commission_percentage}%` : 'Top tier'}</b></div>
            </div>
          </div>
        </div>

        <div className="panel strip">
          <div>
            <span className="ic" style={{ background: 'rgba(22,199,154,.12)', color: '#0D8E6E' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            <div><b className="num">{counts?.active || 0}</b><small>Active client{(counts?.active || 0) === 1 ? '' : 's'} earning commission</small></div>
          </div>
          <div>
            <span className="ic" style={{ background: 'rgba(226,154,11,.13)', color: '#A8730A' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
            </span>
            <div><b className="num">{counts?.pending || 0}</b><small>Waiting on client approval</small></div>
          </div>
          <div>
            <span className="ic" style={{ background: 'rgba(47,107,255,.1)', color: '#2456D6' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3.5 7l8.5 6 8.5-6"/></svg>
            </span>
            <div><b className="num">{counts?.unlinked || 0}</b><small>Invited, not signed up yet</small></div>
          </div>
        </div>

        <div className="two">
          <div className="panel">
            <div className="pane-h">
              <div><h3>Where your money is</h3><p>{rupees(splitTotal)} across {overview.statusSplit.length} cycle{overview.statusSplit.length === 1 ? '' : 's'}</p></div>
            </div>
            <div className="split">
              {overview.statusSplit.length === 0 ? (
                <p className="muted" style={{ fontSize: 13.5 }}>Nothing outstanding right now.</p>
              ) : (
                <>
                  <div className="split-bar">
                    {overview.statusSplit.map((s) => (
                      <i key={s.status} style={{ flex: s.amountPaise, background: (SPLIT_META[s.status] || {}).color || 'var(--text-3)' }} />
                    ))}
                  </div>
                  <div className="split-list">
                    {overview.statusSplit.map((s) => {
                      const meta = SPLIT_META[s.status] || { label: s.status, color: 'var(--text-3)' };
                      return (
                        <div className="split-row" key={s.status}>
                          <i style={{ background: meta.color }} />
                          <span>{meta.label}</span>
                          <b className="num">{rupees(s.amountPaise)}</b>
                          <em>{Math.round((s.amountPaise / splitTotal) * 100)}%</em>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="pane-h"><div><h3>Recent activity</h3><p>Latest updates</p></div></div>
            <div className="feed">
              {overview.activity.length === 0 ? (
                <p className="muted" style={{ fontSize: 13.5 }}>Nothing yet — activity shows up here as clients and cycles change.</p>
              ) : (
                overview.activity.map((a, i) => {
                  const meta = ACTIVITY_META[`${a.type}:${a.status}`] || ACTIVITY_DEFAULT;
                  return (
                  <div className="fitem" key={i}>
                    <span className="fdot" style={{ background: meta.bg, color: meta.fg }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">{ICON[meta.icon]}</svg>
                    </span>
                    <div>
                      <p>{a.text}</p>
                      <small>{new Date(a.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</small>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
